// Ported from dev-scrips-pm-react src/shared/ui/components/page-title.tsx (PROD-1119 Patients UI port).
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

const SIZE_CLASS = {
  base: 'text-base',
  md: 'text-md',
  lg: 'text-lg',
  xl: 'text-xl',
  '2xl': 'text-2xl',
} as const;

export type PageTitleSize = keyof typeof SIZE_CLASS;

export interface PageTitleProps {
  children: ReactNode;
  /**
   * Type scale. Default `'2xl'` (full-page launchpad / list titles).
   * Compact side-panel and edit-shell headers use `'md'` / `'lg'`.
   */
  size?: PageTitleSize;
  className?: string;
}

/**
 * PageTitle — the canonical page / shell heading.
 *
 * Single source of truth for the Scrips page-title look: Blue/700
 * (`text-brand-depth`, #004795) + `font-semibold`. Introduced 2026-06-10
 * after the billing module (dark blue) and patients / scheduling
 * (`text-text-primary` gray) drifted apart — every page hand-rolled its
 * own `<h1>` with no shared colour. Render page and shell titles through
 * this so the colour + weight can't diverge again; only `size` varies by
 * context (full page vs compact panel header).
 */
export function PageTitle({ children, size = '2xl', className }: PageTitleProps) {
  return (
    <h1
      className={cn(
        'font-semibold text-brand-depth',
        SIZE_CLASS[size],
        className,
      )}
    >
      {children}
    </h1>
  );
}
